import { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, ListGroup, Badge, Alert } from "react-bootstrap";
import { t } from "i18next";
import { Folder, Share } from "../../../../interfaces/folder";
import { Document } from "../../../../interfaces/document";
import { shareFolder, unshareFolder, getFolders } from "../../../../services/folder";
import { shareDocument, unshareDocument, getDocuments } from "../../../../services/document";
import { getClubOptions } from "../../../../services/club";
import { getTeamOptions } from "../../../../services/team";
import { getUser } from "../../../../services/user";
import { getStaff } from "../../../../services/staff";
import { getAthlete } from "../../../../services/athlete";
import { getSeasonCurrent } from "../../../../services/season";
import { UserContext } from "../../../../App";

interface ShareModalProps {
    show: boolean;
    onClose: () => void;
    onShare: () => void;
    type: 'folder' | 'document';
    item: Folder | Document;
}

export function ShareModal({ show, onClose, onShare, type, item }: ShareModalProps) {
    const { userProfile } = useContext(UserContext);
    const [shareType, setShareType] = useState<'club' | 'team' | 'manager' | 'coach' | 'athlete'>('club');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentSeasonId, setCurrentSeasonId] = useState<number>(0);
    const [currentShares, setCurrentShares] = useState<Share[]>([]);

    // Filter options
    const [selectedClubFilter, setSelectedClubFilter] = useState<number>(0);
    const [selectedTeamFilter, setSelectedTeamFilter] = useState<number>(0);

    // Available options
    const [clubs, setClubs] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>([]);
    const [managers, setManagers] = useState<any[]>([]);
    const [coaches, setCoaches] = useState<any[]>([]);
    const [athletes, setAthletes] = useState<any[]>([]);

    // Selected items to share with
    const [selectedClubs, setSelectedClubs] = useState<Set<number>>(new Set());
    const [selectedTeams, setSelectedTeams] = useState<Set<number>>(new Set());
    const [selectedManagers, setSelectedManagers] = useState<Set<number>>(new Set());
    const [selectedCoaches, setSelectedCoaches] = useState<Set<number>>(new Set());
    const [selectedAthletes, setSelectedAthletes] = useState<Set<number>>(new Set());

    // User role detection
    const isManager = userProfile.role.filter((r: any) => r.id === 'padeltech_manager').length > 0;
    const isCoach = userProfile.role.filter((r: any) => r.id === 'padeltech_coach').length > 0;
    const isAthlete = userProfile.role.filter((r: any) => r.id === 'padeltech_athlete').length > 0;
    const userClubsRaw = userProfile.club || [];
    // Extract club IDs as numbers for filtering
    const userClubIds = userClubsRaw.map((club: any) => parseInt(club.id));

    useEffect(() => {
        if (show) {
            // Reset selections
            setSelectedClubs(new Set());
            setSelectedTeams(new Set());
            setSelectedManagers(new Set());
            setSelectedCoaches(new Set());
            setSelectedAthletes(new Set());
            setSelectedClubFilter(0);
            setSelectedTeamFilter(0);
            setError('');
            setSuccess('');

            // Load current shares
            setCurrentShares(item.shares || []);

            // Load current season first
            getSeasonCurrent().then(res => {
                const seasonId = res.data && res.data.id ? parseInt(res.data.id) : 0;
                setCurrentSeasonId(seasonId);

                // Load clubs
                loadClubs(seasonId);

                // Load initial data based on shareType
                if (shareType === 'team') {
                    loadTeams(seasonId);
                } else if (shareType === 'manager') {
                    loadTeams(seasonId);
                    // Load managers directly - NO FILTRAR al inicio, mostrar todos
                    getUser('', '', 'padeltech_manager').then(res => {
                        // No filtrar, mostrar todos los managers
                        setManagers(res.data);
                    }).catch(err => {
                    });
                } else if (shareType === 'coach') {
                    loadTeams(seasonId);
                    // Load coaches directly - NO FILTRAR al inicio, mostrar todos
                    getStaff('', '', seasonId.toString(), 'name', 'ASC').then(res => {
                        // No filtrar, mostrar todos los coaches
                        setCoaches(res.data);
                    }).catch(err => {
                    });
                } else if (shareType === 'athlete') {
                    loadTeams(seasonId);
                    // Load athletes directly - NO FILTRAR al inicio, mostrar todos
                    getAthlete('', '', '', seasonId.toString(), 'name', 'ASC', 1, 1000).then(res => {
                        // No filtrar, mostrar todos los athletes
                        setAthletes(res.data);
                    }).catch(err => {
                    });
                }
            }).catch(err => {
            });
        }
    }, [show]);

    // Reload when shareType changes
    useEffect(() => {
        if (!show || currentSeasonId === 0) {
            return;
        }

        if (shareType === 'club') {
            loadClubs(currentSeasonId);
        } else if (shareType === 'team') {
            loadTeams(currentSeasonId);
        } else if (shareType === 'manager') {
            loadTeams(currentSeasonId);
            // Load managers directly - NO FILTRAR, mostrar todos
            getUser('', '', 'padeltech_manager').then(res => {
                // No filtrar, mostrar todos los managers
                setManagers(res.data);
            }).catch(err => {
            });
        } else if (shareType === 'coach') {
            loadTeams(currentSeasonId);
            // Load coaches directly - NO FILTRAR, mostrar todos
            getStaff('', '', currentSeasonId.toString(), 'name', 'ASC').then(res => {
                // No filtrar, mostrar todos los coaches
                setCoaches(res.data);
            }).catch(err => {
            });
        } else if (shareType === 'athlete') {
            loadTeams(currentSeasonId);
            // Load athletes directly - NO FILTRAR, mostrar todos
            getAthlete('', '', '', currentSeasonId.toString(), 'name', 'ASC', 1, 1000).then(res => {
                // No filtrar, mostrar todos los athletes
                setAthletes(res.data);
            }).catch(err => {
            });
        }
    }, [shareType]);

    // Reload when filters change
    useEffect(() => {
        if (!show || currentSeasonId === 0) return;

        if (shareType === 'team') {
            loadTeams(currentSeasonId);
        } else if (shareType === 'manager') {
            loadTeams(currentSeasonId);
            // Reload managers with filters - pasar el club seleccionado al servicio
            const clubParam = selectedClubFilter > 0 ? selectedClubFilter.toString() : '';
            getUser('', clubParam, 'padeltech_manager').then(res => {
                // Si hay club seleccionado, el backend ya filtra, no filtrar aquí
                // Si NO hay club seleccionado (clubParam=''), mostrar todos
                setManagers(res.data);
            }).catch(err => {
            });
        } else if (shareType === 'coach') {
            loadTeams(currentSeasonId);
            // Reload coaches with filters - pasar el club seleccionado al servicio
            const clubParam = selectedClubFilter > 0 ? selectedClubFilter.toString() : '';
            getStaff('', clubParam, currentSeasonId.toString(), 'name', 'ASC').then(res => {
                // Si hay club seleccionado, el backend ya filtra, no filtrar aquí
                // Si NO hay club seleccionado (clubParam=''), mostrar todos
                setCoaches(res.data);
            }).catch(err => {
            });
        } else if (shareType === 'athlete') {
            loadTeams(currentSeasonId);
            // Reload athletes with filters - pasar club y equipo seleccionado al servicio
            const clubParam = selectedClubFilter > 0 ? selectedClubFilter.toString() : '';
            const teamParam = selectedTeamFilter > 0 ? selectedTeamFilter.toString() : '';
            getAthlete('', clubParam, teamParam, currentSeasonId.toString(), 'name', 'ASC', 1, 1000).then(res => {
                // Si hay filtros, el backend ya filtra, no filtrar aquí
                // Si NO hay filtros, mostrar todos
                setAthletes(res.data);
            }).catch(err => {
            });
        }
    }, [selectedClubFilter, selectedTeamFilter]);

    const loadClubs = (seasonId: number) => {
        getClubOptions().then(res => {
            // Si el usuario no tiene clubes en su perfil, mostrar todos (el backend validará)
            let filteredClubs = res.data;
            if (userClubIds.length > 0) {
                filteredClubs = res.data.filter((club: any) => {
                    const clubId = typeof club.id === 'string' ? parseInt(club.id) : club.id;
                    return userClubIds.includes(clubId);
                });
            }
            setClubs(filteredClubs);
        }).catch(err => {
            setError(t('Error loading clubs'));
        });
    };

    const loadTeams = (seasonId: number) => {
        // Load teams for current season, optionally filtered by selected club
        const clubParam = selectedClubFilter > 0 ? selectedClubFilter : 0;
        getTeamOptions(seasonId, clubParam).then(res => {
            let filteredTeams = res.data;

            // If no club filter and user has clubs, only show teams from user's clubs
            if (selectedClubFilter === 0 && userClubIds.length > 0) {
                filteredTeams = filteredTeams.filter((team: any) => {
                    const teamClubId = typeof team.club_id === 'string' ? parseInt(team.club_id) : team.club_id;
                    return userClubIds.includes(teamClubId);
                });
            }

            setTeams(filteredTeams);
        }).catch(err => {
            setError(t('Error loading teams'));
        });
    };

    const handleCheckboxChange = (id: number, currentSet: Set<number>, setter: Function) => {
        const newSet = new Set(currentSet);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setter(newSet);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // El backend validará los permisos, no necesitamos validar userClubIds aquí
        // porque para algunos roles (como entrenadores) puede estar vacío en el perfil

        let itemsToShare: Array<{type: string, id: number}> = [];

        // Collect selected items based on share type
        if (shareType === 'club') {
            if (selectedClubs.size === 0) {
                setError(t('Please select at least one club'));
                return;
            }
            itemsToShare = Array.from(selectedClubs).map(id => ({ type: 'club', id }));
        } else if (shareType === 'team') {
            if (selectedTeams.size === 0) {
                setError(t('Please select at least one team'));
                return;
            }
            itemsToShare = Array.from(selectedTeams).map(id => ({ type: 'team', id }));
        } else if (shareType === 'manager') {
            if (selectedManagers.size === 0) {
                setError(t('Please select at least one manager'));
                return;
            }
            itemsToShare = Array.from(selectedManagers).map(id => {
                const manager = managers.find(m => m.id === id);
                const userId = manager?.user_id || manager?.id;
                return { type: 'user', id: userId };
            });
        } else if (shareType === 'coach') {
            if (selectedCoaches.size === 0) {
                setError(t('Please select at least one coach'));
                return;
            }
            itemsToShare = Array.from(selectedCoaches).map(id => {
                const coach = coaches.find(c => c.id === id);
                const userId = coach?.user_id || coach?.id;
                return { type: 'user', id: userId };
            });
        } else if (shareType === 'athlete') {
            if (selectedAthletes.size === 0) {
                setError(t('Please select at least one athlete'));
                return;
            }
            itemsToShare = Array.from(selectedAthletes).map(id => {
                const athlete = athletes.find(a => a.id === id);
                const userId = athlete?.user_id || athlete?.id;
                return { type: 'user', id: userId };
            });
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Share with each selected item
            for (const shareTarget of itemsToShare) {
                // Determine share_role based on shareType
                let share_role = null;
                if (shareType === 'manager') {
                    share_role = 'padeltech_manager';
                } else if (shareType === 'coach') {
                    share_role = 'padeltech_coach';
                } else if (shareType === 'athlete') {
                    share_role = 'padeltech_athlete';
                }

                const body = {
                    share_type: shareTarget.type,
                    share_target_id: shareTarget.id,
                    share_role: share_role,
                    permission: 'read' as const
                };

                if (type === 'folder') {
                    await shareFolder(item.id, body);
                } else {
                    await shareDocument(item.id, body);
                }
            }

            // Clear selections
            setSelectedClubs(new Set());
            setSelectedTeams(new Set());
            setSelectedManagers(new Set());
            setSelectedCoaches(new Set());
            setSelectedAthletes(new Set());

            // Show success message
            setSuccess(t('Share added successfully'));

            // Reload shares to update the list
            onShare();

            // Update current shares by reloading from backend
            // We need to reload the item to get updated shares
            if (type === 'folder') {
                const updatedFolder = await getFolders(null);
                const folder = updatedFolder.data.find((f: Folder) => f.id === item.id);
                if (folder) {
                    setCurrentShares(folder.shares || []);
                }
            } else {
                const updatedDocs = await getDocuments(null);
                const doc = updatedDocs.data.find((d: Document) => d.id === item.id);
                if (doc) {
                    setCurrentShares(doc.shares || []);
                }
            }

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
            const message = err.response?.data?.message || t('Error sharing item');
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleUnshare = async (shareId: number) => {
        if (!window.confirm(t('Are you sure you want to remove this share?'))) {
            return;
        }

        setError('');
        setSuccess('');

        try {
            if (type === 'folder') {
                await unshareFolder(item.id, shareId);
            } else {
                await unshareDocument(item.id, shareId);
            }

            // Show success message
            setSuccess(t('Share removed successfully'));

            // Reload shares to update the list
            onShare();

            // Update current shares by reloading from backend
            if (type === 'folder') {
                const updatedFolder = await getFolders(null);
                const folder = updatedFolder.data.find((f: Folder) => f.id === item.id);
                if (folder) {
                    setCurrentShares(folder.shares || []);
                }
            } else {
                const updatedDocs = await getDocuments(null);
                const doc = updatedDocs.data.find((d: Document) => d.id === item.id);
                if (doc) {
                    setCurrentShares(doc.shares || []);
                }
            }

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
            const message = err.response?.data?.message || t('Error removing share');
            setError(message);
        }
    };

    const getShareTypeName = (shareType: string) => {
        switch(shareType) {
            case 'user': return t('User');
            case 'club': return t('Club');
            case 'team': return t('Team');
            case 'club_role': return t('Club Role');
            case 'team_role': return t('Team Role');
            default: return shareType;
        }
    };

    const getRoleName = (role: string | null) => {
        if (!role) return '';
        switch(role) {
            case 'padeltech_manager':
            case 'manager':
                return t('Manager');
            case 'padeltech_coach':
            case 'coach':
                return t('Coach');
            case 'padeltech_athlete':
            case 'athlete':
                return t('Athlete');
            default: return role;
        }
    };

    const getInfoMessage = () => {
        if (isManager) {
            return t('As a manager, you can only share with coaches and athletes from your clubs.');
        }
        if (isCoach) {
            return t('As a coach, you can only share with users from your club.');
        }
        if (isAthlete) {
            return t('As an athlete, you cannot share documents. You can only view documents shared with you.');
        }
        return '';
    };

    // If athlete, show info message and don't allow sharing
    if (isAthlete) {
        return (
            <Modal show={show} onHide={onClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('Share')} {type === 'folder' ? t('Folder') : t('Document')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {getInfoMessage()}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('Share')} {type === 'folder' ? t('Folder') : t('Document')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    {getInfoMessage()}
                </Alert>

                {success && (
                    <Alert variant="success">
                        <i className="bi bi-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger">
                        <i className="bi bi-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                <h6 className="mb-3">{t('Add New Share')}</h6>
                <Form onSubmit={handleSubmit}>
                    {/* Share Type Radio Buttons */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Share with')}</Form.Label>
                        <div>
                            <Form.Check
                                type="radio"
                                id="share-club"
                                label={t('Club')}
                                checked={shareType === 'club'}
                                onChange={() => setShareType('club')}
                                disabled={loading}
                                inline
                            />
                            <Form.Check
                                type="radio"
                                id="share-team"
                                label={t('Team')}
                                checked={shareType === 'team'}
                                onChange={() => setShareType('team')}
                                disabled={loading}
                                inline
                            />
                            <Form.Check
                                type="radio"
                                id="share-manager"
                                label={t('Manager')}
                                checked={shareType === 'manager'}
                                onChange={() => setShareType('manager')}
                                disabled={loading}
                                inline
                            />
                            <Form.Check
                                type="radio"
                                id="share-coach"
                                label={t('Coach')}
                                checked={shareType === 'coach'}
                                onChange={() => setShareType('coach')}
                                disabled={loading}
                                inline
                            />
                            <Form.Check
                                type="radio"
                                id="share-athlete"
                                label={t('Athlete')}
                                checked={shareType === 'athlete'}
                                onChange={() => setShareType('athlete')}
                                disabled={loading}
                                inline
                            />
                        </div>
                    </Form.Group>

                    {/* Club Selection */}
                    {shareType === 'club' && (
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Select clubs to share with')}</Form.Label>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                {clubs.map(club => (
                                    <Form.Check
                                        key={club.id}
                                        type="checkbox"
                                        id={`club-${club.id}`}
                                        label={club.name}
                                        checked={selectedClubs.has(club.id)}
                                        onChange={() => handleCheckboxChange(club.id, selectedClubs, setSelectedClubs)}
                                        disabled={loading}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    )}

                    {/* Team Selection */}
                    {shareType === 'team' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Filter by club')} ({t('optional')})</Form.Label>
                                <Form.Select
                                    value={selectedClubFilter}
                                    onChange={(e) => setSelectedClubFilter(parseInt(e.target.value))}
                                    disabled={loading}
                                >
                                    <option value="0">{t('All clubs')}</option>
                                    {clubs.map(club => (
                                        <option key={club.id} value={club.id}>
                                            {club.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Select teams to share with')}</Form.Label>
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                    {teams.map(team => (
                                        <Form.Check
                                            key={team.id}
                                            type="checkbox"
                                            id={`team-${team.id}`}
                                            label={
                                                <>
                                                    {team.name} <span className="text-muted small">({team.club_name})</span>
                                                </>
                                            }
                                            checked={selectedTeams.has(team.id)}
                                            onChange={() => handleCheckboxChange(team.id, selectedTeams, setSelectedTeams)}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}

                    {/* Manager Selection */}
                    {shareType === 'manager' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Filter by club')} ({t('optional')})</Form.Label>
                                <Form.Select
                                    value={selectedClubFilter}
                                    onChange={(e) => {
                                        setSelectedClubFilter(parseInt(e.target.value));
                                        setSelectedTeamFilter(0);
                                    }}
                                    disabled={loading}
                                >
                                    <option value="0">{t('All clubs')}</option>
                                    {clubs.map(club => (
                                        <option key={club.id} value={club.id}>
                                            {club.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {selectedClubFilter > 0 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Filter by team')} ({t('optional')})</Form.Label>
                                    <Form.Select
                                        value={selectedTeamFilter}
                                        onChange={(e) => setSelectedTeamFilter(parseInt(e.target.value))}
                                        disabled={loading}
                                    >
                                        <option value="0">{t('All teams')}</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Select managers to share with')}</Form.Label>
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                    {managers.map(manager => (
                                        <Form.Check
                                            key={manager.id}
                                            type="checkbox"
                                            id={`manager-${manager.id}`}
                                            label={`${manager.name} (${manager.email})`}
                                            checked={selectedManagers.has(manager.id)}
                                            onChange={() => handleCheckboxChange(manager.id, selectedManagers, setSelectedManagers)}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}

                    {/* Coach Selection */}
                    {shareType === 'coach' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Filter by club')} ({t('optional')})</Form.Label>
                                <Form.Select
                                    value={selectedClubFilter}
                                    onChange={(e) => {
                                        setSelectedClubFilter(parseInt(e.target.value));
                                        setSelectedTeamFilter(0); // Reset team filter when club changes
                                    }}
                                    disabled={loading}
                                >
                                    <option value="0">{t('All clubs')}</option>
                                    {clubs.map(club => (
                                        <option key={club.id} value={club.id}>
                                            {club.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {selectedClubFilter > 0 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Filter by team')} ({t('optional')})</Form.Label>
                                    <Form.Select
                                        value={selectedTeamFilter}
                                        onChange={(e) => setSelectedTeamFilter(parseInt(e.target.value))}
                                        disabled={loading}
                                    >
                                        <option value="0">{t('All teams')}</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Select coaches to share with')}</Form.Label>
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                    {coaches.map(coach => (
                                        <Form.Check
                                            key={coach.id}
                                            type="checkbox"
                                            id={`coach-${coach.id}`}
                                            label={`${coach.name} (${coach.email})`}
                                            checked={selectedCoaches.has(coach.id)}
                                            onChange={() => handleCheckboxChange(coach.id, selectedCoaches, setSelectedCoaches)}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}

                    {/* Athlete Selection */}
                    {shareType === 'athlete' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Filter by club')} ({t('optional')})</Form.Label>
                                <Form.Select
                                    value={selectedClubFilter}
                                    onChange={(e) => {
                                        setSelectedClubFilter(parseInt(e.target.value));
                                        setSelectedTeamFilter(0); // Reset team filter when club changes
                                    }}
                                    disabled={loading}
                                >
                                    <option value="0">{t('All clubs')}</option>
                                    {clubs.map(club => (
                                        <option key={club.id} value={club.id}>
                                            {club.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {selectedClubFilter > 0 && (
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Filter by team')} ({t('optional')})</Form.Label>
                                    <Form.Select
                                        value={selectedTeamFilter}
                                        onChange={(e) => setSelectedTeamFilter(parseInt(e.target.value))}
                                        disabled={loading}
                                    >
                                        <option value="0">{t('All teams')}</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Select athletes to share with')}</Form.Label>
                                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                    {athletes.map(athlete => (
                                        <Form.Check
                                            key={athlete.id}
                                            type="checkbox"
                                            id={`athlete-${athlete.id}`}
                                            label={`${athlete.name} (${athlete.email})`}
                                            checked={selectedAthletes.has(athlete.id)}
                                            onChange={() => handleCheckboxChange(athlete.id, selectedAthletes, setSelectedAthletes)}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}

                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t('Adding...') : t('Add')}
                    </Button>
                </Form>

                <hr className="my-4" />

                <h6 className="mb-3">{t('Current Shares')}</h6>
                {currentShares && currentShares.length > 0 ? (
                    <ListGroup>
                        {currentShares.map((share: Share) => (
                            <ListGroup.Item key={share.id} className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Badge bg="secondary" className="me-2">
                                        {share.share_type === 'user' && share.share_role ? getRoleName(share.share_role) : getShareTypeName(share.share_type)}
                                    </Badge>
                                    <strong>{share.target_name}</strong>
                                </div>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleUnshare(share.id)}
                                >
                                    <i className="bi bi-trash"></i>
                                </Button>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : (
                    <p className="text-muted">{t('Not shared with anyone yet')}</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t('Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
